<template>
  <div class="import-summary-banner" role="status" data-testid="import-summary">
    <i class="pi pi-info-circle summary-icon" aria-hidden="true"></i>
    <div class="summary-content">
      <strong class="summary-title">{{ title }}</strong>
      <p v-for="(line, i) in lines" :key="i" class="summary-line">{{ line }}</p>
    </div>
    <Button v-if="messages.upgrade" :label="$t('importData.summary.upgrade')" size="small"
      class="upgrade-btn" data-testid="import-summary-upgrade" @click="$emit('upgrade')" />
    <Button icon="pi pi-times" class="p-button-text p-button-sm close-btn"
      :aria-label="$t('importData.summary.dismiss')" v-tooltip.bottom="$t('importData.summary.dismiss')"
      @click="$emit('dismiss')" />
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import Button from 'primevue/button'
import { useLocaleFormat } from '../composables/useLocaleFormat.js'
import { importSummaryMessages, formatImportSummaryLine } from '../utils/importSummary.js'

// The result of a bulk import that did not bring in every link: which ones
// were left out and why. Clean imports get a toast instead (App.vue).
export default {
  name: 'ImportSummaryBanner',
  components: { Button },
  props: {
    result: {
      type: Object,
      required: true
    }
  },
  emits: ['dismiss', 'upgrade'],
  setup(props) {
    const { t } = useI18n()
    const { formatNumber } = useLocaleFormat()
    const messages = computed(() => importSummaryMessages(props.result))
    const format = (line) => formatImportSummaryLine(line, t, formatNumber)
    const title = computed(() => format(messages.value.title))
    const lines = computed(() => messages.value.lines.map(format))
    return { messages, title, lines }
  }
}
</script>

<style scoped>
.import-summary-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--accent-light);
  border-bottom: 1px solid var(--border-color);
}

.summary-icon {
  color: var(--accent-color);
  margin-top: 0.15rem;
}

.summary-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.summary-title {
  color: var(--text-primary);
  font-size: 0.9rem;
}

.summary-line {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
  line-height: 1.4;
}

.upgrade-btn {
  flex-shrink: 0;
}

.close-btn {
  color: var(--text-secondary) !important;
  padding: 0.25rem !important;
  flex-shrink: 0;
}

.close-btn:hover {
  color: var(--text-primary) !important;
  background: var(--bg-secondary) !important;
}

@media (max-width: 768px) {
  .import-summary-banner {
    flex-wrap: wrap;
    padding: 0.5rem 0.75rem;
  }

  .summary-content {
    flex-basis: calc(100% - 5rem);
  }

  /* the close button stays on the first row, the upgrade button wraps under the text */
  .upgrade-btn {
    order: 3;
    margin-left: 1.75rem;
  }
}
</style>
